import React from 'react'
import {Link as RouteLink} from 'react-router-dom'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'

// Components
import {
    Accordion,
    AccordionButton,
    AccordionItem,
    AccordionPanel,
    Box,
    Link,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {ListItem, UnorderedList} from '@chakra-ui/react'
import {DrawerMenuLvl3Categories} from './drawer-menu-lvl3-categories'

import {categoryUrlBuilder} from '../../utils/url'
import {ChevronUpIcon, ChevronDownIcon} from '../custom-icons'
import useMultiSite from '../../hooks/use-multi-site'

// Utils
import {idFromString} from '../../utils/utils'

// Custom Hooks
import {useGTM} from '@app/hooks/use-gtm'

/**
 * Navigation menu level 2 categories component for mobile
 *
 * @param {Object} props
 * @param {Object} props.item
 * @param {string} props.itemsKey
 * @param {Function} props.itemsFilter
 * @returns {React.Component}
 */
export const DrawerMenuLvl2CategoriesMobile = ({item, itemsKey, itemsFilter, ...props}) => {
    const items = item[itemsKey] || []
    const {triggerClickMenuEvent} = useGTM()

    const filter = (item) =>
        typeof itemsFilter === 'function' ? itemsFilter(item) : !!item[itemsFilter]

    /**
     * Triggers a click event for a menu item.
     *
     * This function is responsible for invoking the `triggerClickMenuEvent` function
     * with the specified menu item. It is typically used to handle user interactions
     * with menu items in a drawer menu component.
     *
     * @param {Object} itemClicked - The menu item that was clicked. This object
     * should contain relevant information about the menu item, such as its identifier
     * or label, which will be used by the `triggerClickMenuEvent` function to process
     * the click event.
     */
    const triggerClickMenu = (itemClicked) => {
        triggerClickMenuEvent(itemClicked)
    }

    return (
        <Box {...props}>
            <Accordion allowMultiple={true} flex={1} overflowY={'auto'} mt="-1">
                {items.filter(filter).map((itemLvl2) => {
                    const {id, name: itemName} = itemLvl2
                    const headingId = idFromString(itemName, 'lvl2-category')

                    return (
                        <AccordionItem key={id} onClick={() => triggerClickMenu(item)}>
                            {({isExpanded}) => (
                                <>
                                    {/* Heading */}
                                    <AccordionButton
                                        p="20px"
                                        display={'flex'}
                                        alignItems={'center'}
                                        justifyContent={'space-between'}
                                        textTransform={'uppercase'}
                                    >
                                        <Text
                                            variant="eyeBrowSmall"
                                            role="heading"
                                            aria-level="3"
                                            id={headingId}
                                        >
                                            {itemName}
                                        </Text>
                                        {isExpanded ? (
                                            <ChevronUpIcon boxSize={3} aria-hidden="true" />
                                        ) : (
                                            <ChevronDownIcon boxSize={3} aria-hidden="true" />
                                        )}
                                    </AccordionButton>

                                    {/* Child Items */}
                                    {isExpanded && (
                                        <Box mt="-3px">
                                            <AccordionPanel p="3px 20px 13px">
                                                <DrawerMenuLvl3Categories
                                                    item={itemLvl2}
                                                    itemsKey={itemsKey}
                                                    itemsFilter={itemsFilter}
                                                    isOpen={isExpanded}
                                                    ariaDescribedBy={headingId}
                                                />
                                            </AccordionPanel>
                                        </Box>
                                    )}
                                </>
                            )}
                        </AccordionItem>
                    )
                })}
            </Accordion>
        </Box>
    )
}

/**
 * Navigation menu level 2 categories component for desktop
 *
 * @param {Object} props
 * @param {Object} props.item
 * @param {string} props.itemsKey
 * @param {Function} props.itemsFilter
 * @param {boolean} props.isOpen
 * @returns {React.Component}
 */
export const DrawerMenuLvl2CategoriesDesktop = ({
    item,
    itemsKey,
    itemsFilter,
    isOpen,
    ...props
}) => {
    const intl = useIntl()
    const {triggerClickMenuEvent} = useGTM()
    const items = item[itemsKey] || []

    const filter = (item) =>
        typeof itemsFilter === 'function' ? itemsFilter(item) : !!item[itemsFilter]

    const {buildUrl} = useMultiSite()

    /**
     * Triggers a click event for a menu item.
     *
     * This function is responsible for invoking the `triggerClickMenuEvent` function
     * with the specified menu item. It is typically used to handle user interactions
     * with menu items in a drawer menu component.
     *
     * @param {Object} itemClicked - The menu item that was clicked. This object
     * should contain relevant information about the menu item, such as its identifier
     * or label, which will be used by the `triggerClickMenuEvent` function to process
     * the click event.
     */
    const triggerClickMenu = (itemClicked) => {
        triggerClickMenuEvent(itemClicked)
    }

    return (
        <Box {...props} width="349px">
            <Link
                as={RouteLink}
                to={buildUrl(categoryUrlBuilder(item, item?.c_categoryType))}
                display={'block'}
                tabIndex={isOpen ? 0 : -1}
                onClick={() => triggerClickMenu(item)}
            >
                <Text variant={'bodySmall'} fontWeight="500">
                    {intl.formatMessage(
                        {
                            id: 'drawer_menu.link.shop_all',
                            defaultMessage: 'View all {name}'
                        },
                        {
                            name: item?.name || ''
                        }
                    )}
                </Text>
            </Link>
            <UnorderedList styleType="none" ml={0}>
                {items.filter(filter).map((itemLvl2) => {
                    const {id, name: itemLvl2Name} = itemLvl2
                    const headingId = idFromString(itemLvl2Name, 'lvl2-category')

                    return (
                        <ListItem key={id}>
                            {/* Heading */}
                            <Text
                                variant="descriptor"
                                textTransform={'uppercase'}
                                role="heading"
                                aria-level="3"
                                mt="34px"
                                mb="7px"
                                id={headingId}
                            >
                                {itemLvl2Name}
                            </Text>

                            {/* Child Items */}
                            <DrawerMenuLvl3Categories
                                item={itemLvl2}
                                itemsKey={itemsKey}
                                isOpen={isOpen}
                                itemsFilter={itemsFilter}
                                ariaDescribedBy={headingId}
                            />
                        </ListItem>
                    )
                })}
            </UnorderedList>
        </Box>
    )
}

DrawerMenuLvl2CategoriesMobile.propTypes = {
    item: PropTypes.object.isRequired,
    itemsKey: PropTypes.string.isRequired,
    itemsFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
}

DrawerMenuLvl2CategoriesDesktop.propTypes = {
    item: PropTypes.object.isRequired,
    itemsKey: PropTypes.string.isRequired,
    itemsFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    isOpen: PropTypes.bool
}
